import { Component, HostListener, OnInit } from '@angular/core';
import { NoteService } from '../../services/note.service';
import { NavigationHelper } from '../../helpers/navigation';
import { PottyNotesAlertController } from '../../error-handling/alerting/potty-notes-alert-controller';
import { Song } from '../../interfaces/song.interface';
import { GroupedSongs } from '../../../app/interfaces/grouped-songs.interface';
import { PottyNotesGestureController } from '../../utils/gesture.controller';
import { Router } from '@angular/router';


@Component({
    selector: 'choose-song',
    templateUrl: 'choose-song.html',
    styleUrls: ['choose-song.scss'],
    standalone: false
})
export class ChooseSongComponent implements OnInit {
	public static readonly ComponentIdentifier: number = 1;
	public nextButtonText: string;
	public songs: Song[] = [];
	public songsGroupedByKey: GroupedSongs = {};
	public currentIndex = 0;
	public songsCategories: string[] = [];
	public selectedSongId: string = '';
	public selectedSongCategory: string = '';
	public selectedSong: Song | undefined;
	public isStartOfFlow: boolean;
	public isGettingSongs = false;

	private initialTouchX: number | null = null;
	private initialTouchY: number | null = null;
	private touchStartTime: number | null = null;

	constructor(
		private notesService: NoteService,
		private navigationHelper: NavigationHelper,
		private pottyNotesAlertController: PottyNotesAlertController,
		private router: Router) {
	}

	ngOnInit(): void {
		if (this.notesService.songs.length === 0) {
			this.isGettingSongs = true;
			this.notesService.getSongs()
			  .subscribe(
				(songs) => {
				  this.songs = songs;
				  this.songsGroupedByKey = this.groupByKey(this.songs);
				  this.songsCategories = Object.keys(this.songsGroupedByKey);
				  this.isGettingSongs = false;
				},
				(error) => {
				  this.isGettingSongs = false;
				  this.pottyNotesAlertController.displaySongRetrievalFailure();
				}
			  );
		} else {
			this.songs = this.notesService.songs;
			this.songsGroupedByKey = this.groupByKey(this.songs);
			this.songsCategories = Object.keys(this.songsGroupedByKey);

			if (this.notesService.selectedSong !== null) {
				this.setComponentNoteMeta(this.notesService.selectedSong.songId, this.notesService.selectedSong.category);
			}
		}
		this.isStartOfFlow = this.navigationHelper.isStartOfNoteFlow();

		this.nextButtonText = this.notesService.selectedSong === null ? 'skip' : 'next';
	}

	public shuffleSongs() {
		const nextIndex = this.currentIndex + 1 >= this.songsGroupedByKey[this.songsCategories[0]].length ? 0 : this.currentIndex + 1;

		if (this.selectedSongId && this.selectedSongCategory) {
			const selectedSong = this.songsGroupedByKey[this.selectedSongCategory][this.currentIndex];
			const nextSong = this.songsGroupedByKey[this.selectedSongCategory][nextIndex];

			this.songsGroupedByKey[this.selectedSongCategory][this.currentIndex] = nextSong;
			this.songsGroupedByKey[this.selectedSongCategory][nextIndex] = selectedSong;
		}

		this.currentIndex = nextIndex;
	}

	private setComponentNoteMeta(songId: string, songCategory: string) {
		this.selectedSongId = songId;
		this.selectedSongCategory = songCategory; 
	}

	/// <summary>
	/// Toggles the selected song ID and category, and enables/disables the continue button based on the selection.
	/// </summary>
	/// <param name="songId">The ID of the song to select.</param>
	/// <param name="songCategory">The category of the song to select.</param>
 	public toggleSelectedSongId(songId: string, songCategory: string) {
		if (this.selectedSongId && this.selectedSongId === songId) {
			this.selectedSongId = '';
			this.selectedSongCategory = '';
			this.notesService.selectedSong = null;
			this.nextButtonText = 'skip';
			return;
		}
		this.setComponentNoteMeta(songId,  songCategory);;
		this.nextButtonText = 'next';
		// Set the state in the note service.
		this.notesService.selectedSong = this.songs.find(x => x.songId === this.selectedSongId) || null;
	}

	private groupByKey(array: Song[]): GroupedSongs {
		return array.reduce((hash: GroupedSongs, song: Song) => {
			if (song['category'] === undefined) return hash;
			return Object.assign(hash, { [song['category']]: (hash[song['category']] || []).concat(song) });
		}, {} as GroupedSongs);
	}

	public saveSongMetadata(event: Event) {
		this.selectedSong = this.songs.find(x => x.songId === this.selectedSongId);
		const wasSkipped = this.navigationHelper.skipButtonPressed(event) || this.selectedSong === undefined;
		this.notesService.setSong(this.selectedSong, wasSkipped);
	}

	// Gesture control 
	@HostListener('touchstart', ['$event'])
	public onTouchStart(event: TouchEvent): void {
		event.stopPropagation();
		this.initialTouchX = event.touches[0].clientX;
		this.initialTouchY = event.touches[0].clientY;
		this.touchStartTime = Date.now();
	}

	@HostListener('touchmove', ['$event'])
	public onTouchMove(event: TouchEvent): void {
		event.stopPropagation();

		if (this.initialTouchX === null || this.touchStartTime === null || this.initialTouchY == null) return;
 
		const currentTouchX = event.touches[0].clientX;
		const currentTouchY  = event.touches[0].clientY;
		const deltaX = currentTouchX - this.initialTouchX;
		const deltaY = currentTouchY - this.initialTouchY;
		const timeDiff = Date.now() - this.touchStartTime

		// This represents moving backward in the add notes flow.
		if (PottyNotesGestureController.MovedRightEnough(timeDiff, deltaX, deltaY)) {
			this.saveSongMetadata(event);
			this.router.navigate([`${this.navigationHelper.getPreviousComponentUrl()}`]);
		}
		// Otherwise, forward.
		else if (PottyNotesGestureController.MovedLeftEnough(timeDiff, deltaX, deltaY)) {
			this.saveSongMetadata(event);
			this.router.navigate([`${this.navigationHelper.getNextComponentUrl()}`]);
		}
	}

	@HostListener('touchend', ['$event'])
	public onTouchEnd(event: TouchEvent): void {
		event.stopPropagation();
	}
}
